import { apiRequest, HttpError } from "../utils/http";

export async function fetchCandidateGroups(date) {
  const rows = await apiRequest(`/api/transport/candidateGroups?date=${encodeURIComponent(date)}`);
  return Array.isArray(rows) ? rows : [];
}

export async function createTransportGroup(payload) {
  return apiRequest("/api/transport/groups", {
    method: "POST",
    body: payload,
  });
}

export async function updateTransportGroup(id, payload) {
  return apiRequest(`/api/transport/groups/${id}`, {
    method: "PUT",
    body: payload,
  });
}

export async function confirmTransportGroup(id) {
  return apiRequest(`/api/transport/groups/${id}/confirm`, {
    method: "PUT",
  });
}

export async function unconfirmTransportGroup(id) {
  return apiRequest(`/api/transport/groups/${id}/unconfirm`, {
    method: "PUT",
  });
}

export async function deleteTransportGroup(id) {
  return apiRequest(`/api/transport/groups/${id}?confirm=true`, {
    method: "DELETE",
  });
}

export async function updateTransportGroupPassengers(busId, passengers) {
  return apiRequest(`/api/transport/groups/${busId}/passengers`, {
    method: "PUT",
    body: {
      passengers: (passengers || []).map((p) => {
        const type = String(p?.personType || "").toUpperCase();
        const id =
          type === "ADULT"
            ? (p?.adultId ?? p?.id)
            : (p?.studentId ?? p?.id);

        return { personType: type, id };
      }),
    },
  });
}

/**
 * Higher-level save used by CampusTransportBoard.
 * Handles add vs edit and optional confirm in one place.
 */
export async function saveTransportGroup({ mode, id, payload }) {
  if (mode === "add") {
    return createTransportGroup(payload);
  }

  const { confirm, unconfirm, ...updatePayload } = payload || {};

  // Unconfirm first (a clean state transition owned by the backend, reverting
  // the bus to Planned), then apply the edits. This lets the user unconfirm and
  // change capacity/route in one save. The update payload never sends
  // "Confirmed" as status, so it won't be rejected or re-assert confirmation.
  if (unconfirm === true) {
    await unconfirmTransportGroup(id);
    await updateTransportGroup(id, updatePayload);
    return { ok: true, unconfirmed: true };
  }

  await updateTransportGroup(id, updatePayload);

  if (confirm === true) {
    await confirmTransportGroup(id);
  }

  return {
    ok: true,
    confirmed: confirm === true,
  };
}

/**
 * Small helper to map transport API errors to UI-safe messages.
 */
export function getTransportErrorMessage(error, fallback = "Request failed") {
  const xerr = error?.headers?.get?.("X-Error");

  if (xerr === "CONTACT_REQUIRED") {
    return "Contact name and number are required to confirm.";
  }

  if (xerr === "CAPACITY_EXCEEDED") {
    return "Bus capacity exceeded.";
  }

  // The bus is someone's attestation that a vehicle of that capacity is booked
  // on that route; the server refuses changes under the stamp.
  if (xerr === "CONFIRMED_LOCKED") {
    return "This bus is confirmed. Unconfirm it first, then make the change.";
  }

  // Editing transport needs the TRANSPORT grant; reading it does not.
  if (error?.status === 403) {
    return "You do not have permission to change transport. Ask an administrator for the Transport grant.";
  }

  return error?.message || fallback;
}

export function isTransportError(error, code) {
  return error?.headers?.get?.("X-Error") === code;
}

export { HttpError };